/* Original Members homepage: textured Three.js Earth, space effects and UI motion. */
class OMCGlobe {
  constructor(root) {
    this.root = root;
    this.spaceCanvas = root.querySelector('[data-space-canvas]');
    this.ctx = this.spaceCanvas.getContext('2d', { alpha: true });
    this.webglHost = root.querySelector('[data-globe-webgl]');
    this.label = root.querySelector('[data-globe-label]');
    this.config = JSON.parse(root.querySelector('[data-globe-config]').textContent);
    this.cities = this.config.cities;
    this.active = 0;
    this.hovering = false;
    this.reduced = matchMedia('(prefers-reduced-motion: reduce)').matches;
    this.lastTime = 0;
    this.ready = false;
    this.buildSpace();
    this.bindUI();
    this.resize();
    this.initThree();
    this.setActive(0, false);
    this.frame(0);
  }

  async initThree() {
    try {
      const THREE = await import(this.config.threeUrl);
      this.THREE = THREE;
      const mobile = this.w < 750;
      const renderer = new THREE.WebGLRenderer({
        alpha: true,
        antialias: !mobile,
        powerPreference: 'high-performance'
      });
      renderer.setPixelRatio(Math.min(devicePixelRatio || 1, mobile ? 1.35 : 1.75));
      renderer.setSize(this.w, this.h);
      renderer.outputColorSpace = THREE.SRGBColorSpace;
      renderer.toneMapping = THREE.ACESFilmicToneMapping;
      renderer.toneMappingExposure = 1.08;
      renderer.setClearColor(0x000000, 0);
      this.webglHost.appendChild(renderer.domElement);
      this.renderer = renderer;

      this.scene = new THREE.Scene();
      this.camera = new THREE.PerspectiveCamera(42, this.w / this.h, .1, 100);
      this.camera.position.set(0, 0, 3.15);

      this.globeGroup = new THREE.Group();
      this.globeGroup.position.x = mobile ? .28 : .33;
      this.scene.add(this.globeGroup);

      const loader = new THREE.TextureLoader();
      const loadTexture = (url) => new Promise((resolve, reject) => loader.load(url, resolve, undefined, reject));
      const [earthTexture, normalTexture, cloudTexture] = await Promise.all([
        loadTexture(this.config.earthUrl),
        loadTexture(this.config.normalUrl),
        loadTexture(this.config.cloudUrl)
      ]);
      earthTexture.colorSpace = THREE.SRGBColorSpace;
      earthTexture.anisotropy = Math.min(8, renderer.capabilities.getMaxAnisotropy());
      normalTexture.anisotropy = Math.min(4, renderer.capabilities.getMaxAnisotropy());
      cloudTexture.colorSpace = THREE.SRGBColorSpace;

      const segments = mobile ? 48 : 72;
      const sphere = new THREE.SphereGeometry(1, segments, segments);
      const earthMaterial = new THREE.MeshPhongMaterial({
        map: earthTexture,
        normalMap: normalTexture,
        normalScale: new THREE.Vector2(.28, .28),
        emissive: new THREE.Color(0xaaa36f),
        emissiveMap: earthTexture,
        emissiveIntensity: .52,
        shininess: 4,
        specular: new THREE.Color(0x172329)
      });
      this.earth = new THREE.Mesh(sphere, earthMaterial);
      this.globeGroup.add(this.earth);

      const cloudMaterial = new THREE.MeshPhongMaterial({
        map: cloudTexture,
        alphaMap: cloudTexture,
        color: 0xaebec2,
        transparent: true,
        opacity: .12,
        depthWrite: false,
        blending: THREE.AdditiveBlending
      });
      this.clouds = new THREE.Mesh(new THREE.SphereGeometry(1.008, segments, segments), cloudMaterial);
      this.globeGroup.add(this.clouds);

      const atmosphereMaterial = new THREE.ShaderMaterial({
        transparent: true,
        side: THREE.BackSide,
        blending: THREE.AdditiveBlending,
        depthWrite: false,
        vertexShader: `
          varying vec3 vNormal;
          varying vec3 vPosition;
          void main() {
            vNormal = normalize(normalMatrix * normal);
            vec4 viewPosition = modelViewMatrix * vec4(position, 1.0);
            vPosition = viewPosition.xyz;
            gl_Position = projectionMatrix * viewPosition;
          }
        `,
        fragmentShader: `
          varying vec3 vNormal;
          varying vec3 vPosition;
          void main() {
            vec3 viewDirection = normalize(-vPosition);
            float rim = pow(1.0 - max(dot(vNormal, viewDirection), 0.0), 4.8);
            gl_FragColor = vec4(0.20, 0.38, 0.42, rim * 0.12);
          }
        `
      });
      this.atmosphere = new THREE.Mesh(new THREE.SphereGeometry(1.018, segments, segments), atmosphereMaterial);
      this.globeGroup.add(this.atmosphere);

      this.scene.add(new THREE.AmbientLight(0x66808a, 1.05));
      const keyLight = new THREE.DirectionalLight(0x9fc5d0, 1.15);
      keyLight.position.set(-2.5, 2.2, 3.5);
      this.scene.add(keyLight);
      const rimLight = new THREE.DirectionalLight(0x315d70, .8);
      rimLight.position.set(3, -1, -2);
      this.scene.add(rimLight);

      this.createMarkers();
      this.raycaster = new THREE.Raycaster();
      this.pointer = new THREE.Vector2();
      this.bindGlobePointer();
      this.targetQuaternion = new THREE.Quaternion();
      this.setActive(this.active, true, true);
      this.ready = true;
      this.root.classList.add('omc-map-loaded');
    } catch (error) {
      this.root.classList.add('omc-globe-fallback');
      console.warn('OMC textured globe could not initialize.', error);
    }
  }

  latLonVector(lat, lon, radius = 1) {
    const THREE = this.THREE;
    const phi = (90 - Number(lat)) * Math.PI / 180;
    const theta = (Number(lon) + 180) * Math.PI / 180;
    return new THREE.Vector3(
      -radius * Math.sin(phi) * Math.cos(theta),
      radius * Math.cos(phi),
      radius * Math.sin(phi) * Math.sin(theta)
    );
  }

  populationWeight(name) {
    const weights = {
      'New York': 1.25, 'Los Angeles': 1.12, 'Chicago': .96, 'Dallas': .9,
      'Atlanta': .82, 'Miami': .88, 'London': 1.2, 'Paris': 1.1,
      'Tokyo': 1.4, 'Dubai': 1.02, 'Lagos': 1.3, 'Johannesburg': .94,
      'Toronto': .96, 'Mexico City': 1.34, 'Sao Paulo': 1.38, 'Cairo': 1.22,
      'Istanbul': 1.25, 'Melbourne': .9
    };
    return weights[name] || .85;
  }

  createMarkers() {
    const THREE = this.THREE;
    this.markerObjects = [];
    const color = new THREE.Color(this.config.accent);
    this.cities.forEach((city, index) => {
      const weight = this.populationWeight(city.name);
      const group = new THREE.Group();
      const position = this.latLonVector(city.lat, city.lon, 1.013);
      group.position.copy(position);
      group.quaternion.setFromUnitVectors(new THREE.Vector3(0, 0, 1), position.clone().normalize());

      const dot = new THREE.Mesh(
        new THREE.SphereGeometry(.011 * weight, 12, 12),
        new THREE.MeshBasicMaterial({ color, transparent: true, opacity: .86 })
      );
      dot.userData.cityIndex = index;
      group.add(dot);

      const ring = new THREE.Mesh(
        new THREE.RingGeometry(.022 * weight, .026 * weight, 28),
        new THREE.MeshBasicMaterial({
          color,
          transparent: true,
          opacity: .34,
          side: THREE.DoubleSide,
          depthWrite: false
        })
      );
      ring.position.z = .001;
      group.add(ring);

      const halo = new THREE.Mesh(
        new THREE.RingGeometry(.031 * weight, .054 * weight, 28),
        new THREE.MeshBasicMaterial({
        color,
        transparent: true,
        opacity: .1,
        side: THREE.DoubleSide,
        blending: THREE.AdditiveBlending,
        depthWrite: false
        })
      );
      halo.position.z = .002;
      group.add(halo);

      this.globeGroup.add(group);
      this.markerObjects.push({ group, dot, ring, halo, weight, city });
    });
  }

  bindGlobePointer() {
    this.webglHost.addEventListener('pointermove', (event) => {
      if (!this.ready) return;
      const rect = this.webglHost.getBoundingClientRect();
      this.pointer.x = ((event.clientX - rect.left) / rect.width) * 2 - 1;
      this.pointer.y = -((event.clientY - rect.top) / rect.height) * 2 + 1;
      this.raycaster.setFromCamera(this.pointer, this.camera);
      const hits = this.raycaster.intersectObjects(this.markerObjects.map((marker) => marker.dot), false);
      if (hits.length) {
        this.hovering = true;
        this.setActive(hits[0].object.userData.cityIndex, true);
        this.webglHost.style.cursor = 'pointer';
      } else {
        this.hovering = false;
        this.webglHost.style.cursor = '';
      }
    }, { passive: true });
    this.webglHost.addEventListener('pointerleave', () => {
      this.hovering = false;
      this.webglHost.style.cursor = '';
    });
  }

  buildSpace() {
    this.stars = [];
    this.shootingStars = [];
    this.fallingStars = [];
    this.meteorShower = [];
    this.nextShower = performance.now() + 4200;
    this.ufo = { start: performance.now() + 6500, duration: 5600 };

    for (let i = 0; i < 165; i++) {
      this.stars.push({
        x: this.seed(i * 3.1), y: this.seed(i * 7.7),
        r: .25 + this.seed(i * 11.3), alpha: .1 + this.seed(i * 5.9) * .4
      });
    }
    for (let i = 0; i < 5; i++) this.resetShootingStar({ seed: i }, i * 1800 + 1200);
    for (let i = 0; i < 14; i++) {
      this.fallingStars.push({
        x: this.seed(i * 9.7), y: this.seed(i * 5.3) * .65,
        speed: .000006 + this.seed(i * 2.8) * .000012,
        alpha: .05 + this.seed(i * 7.1) * .16
      });
    }
  }

  seed(value) {
    return Math.abs(Math.sin(value + 1) * 43758.5453) % 1;
  }

  bindUI() {
    addEventListener('resize', () => this.resize(), { passive: true });
    this.root.addEventListener('pointermove', (event) => {
      const rect = this.root.getBoundingClientRect();
      this.root.style.setProperty('--pointer-x', `${(event.clientX / rect.width - .5) * 13}px`);
      this.root.style.setProperty('--pointer-y', `${(event.clientY / rect.height - .5) * 13}px`);
    }, { passive: true });
    this.root.querySelectorAll('[data-city-trigger]').forEach((button) => {
      const activate = () => {
        this.hovering = true;
        this.setActive(Number(button.dataset.cityTrigger), true);
      };
      button.addEventListener('mouseenter', activate);
      button.addEventListener('focus', activate);
      button.addEventListener('click', activate);
      button.addEventListener('mouseleave', () => { this.hovering = false; });
      button.addEventListener('blur', () => { this.hovering = false; });
    });
    this.timer = setInterval(() => {
      if (!this.hovering && !document.hidden) this.setActive((this.active + 1) % this.cities.length, true);
    }, 5200);
  }

  resize() {
    const dpr = Math.min(devicePixelRatio || 1, 1.75);
    this.w = this.root.clientWidth;
    this.h = this.root.clientHeight;
    this.spaceCanvas.width = this.w * dpr;
    this.spaceCanvas.height = this.h * dpr;
    this.spaceCanvas.style.width = `${this.w}px`;
    this.spaceCanvas.style.height = `${this.h}px`;
    this.ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    if (this.renderer && this.camera) {
      this.renderer.setSize(this.w, this.h);
      this.camera.aspect = this.w / this.h;
      this.camera.updateProjectionMatrix();
      this.globeGroup.position.x = this.w < 750 ? .28 : .33;
    }
  }

  setActive(index, rotate = true, immediate = false) {
    const city = this.cities[index];
    if (!city) return;
    this.active = index;

    if (this.ready && rotate) {
      const THREE = this.THREE;
      const cityDirection = this.latLonVector(city.lat, city.lon, 1).normalize();
      const front = new THREE.Vector3(0, 0, 1);
      this.targetQuaternion = new THREE.Quaternion().setFromUnitVectors(cityDirection, front);
      if (immediate || this.reduced) this.globeGroup.quaternion.copy(this.targetQuaternion);
    }

    this.root.querySelector('[data-city-name]').textContent = city.name;
    this.root.querySelector('[data-city-country]').textContent = city.country;
    this.root.querySelector('[data-city-statement]').textContent = city.statement;
    this.root.querySelector('[data-city-index]').textContent = `${String(index + 1).padStart(2, '0')} / ${String(this.cities.length).padStart(2, '0')}`;
    this.root.querySelector('[data-city-coordinate]').textContent =
      `${Math.abs(Number(city.lat)).toFixed(4)}\u00b0 ${Number(city.lat) >= 0 ? 'N' : 'S'} / ${Math.abs(Number(city.lon)).toFixed(4)}\u00b0 ${Number(city.lon) >= 0 ? 'E' : 'W'}`;
    this.root.querySelectorAll('[data-city-visual]').forEach((visual, visualIndex) => visual.classList.toggle('is-active', visualIndex === index));
    this.root.querySelectorAll('[data-city-trigger]').forEach((button, buttonIndex) => button.classList.toggle('is-active', buttonIndex === index));

    if (this.markerObjects) {
      this.markerObjects.forEach((marker, markerIndex) => {
        const active = markerIndex === index;
        marker.dot.scale.setScalar(active ? 1.7 : 1);
        marker.ring.scale.setScalar(active ? 1.5 : 1);
        marker.ring.material.opacity = active ? .9 : .3;
        marker.halo.material.opacity = active ? .38 : .1;
      });
    }

    const rail = this.root.querySelector('[data-city-rail]');
    const activeButton = rail && rail.querySelector('.is-active');
    if (rail && activeButton) rail.scrollTo({ left: activeButton.offsetLeft - rail.clientWidth / 2 + activeButton.clientWidth / 2, behavior: this.reduced ? 'auto' : 'smooth' });

    const card = this.root.querySelector('[data-city-card]');
    card.classList.remove('is-changing');
    requestAnimationFrame(() => card.classList.add('is-changing'));
  }

  resetShootingStar(star, extraDelay = 0) {
    star.x = -.15 + Math.random() * .68;
    star.y = .02 + Math.random() * .45;
    star.length = 90 + Math.random() * 150;
    star.angle = .22 + Math.random() * .22;
    star.start = performance.now() + extraDelay + 1800 + Math.random() * 5000;
    star.duration = 850 + Math.random() * 600;
    if (!this.shootingStars.includes(star)) this.shootingStars.push(star);
  }

  drawSpace(time) {
    const c = this.ctx;
    c.clearRect(0, 0, this.w, this.h);
    this.stars.forEach((star) => {
      c.fillStyle = `rgba(220,229,225,${star.alpha})`;
      c.beginPath();
      c.arc(star.x * this.w, star.y * this.h, star.r, 0, Math.PI * 2);
      c.fill();
    });
    if (this.reduced) return;

    this.fallingStars.forEach((star) => {
      const y = (star.y * this.h + time * star.speed * this.h) % (this.h * .78);
      const x = star.x * this.w + y * .035;
      const gradient = c.createLinearGradient(x, y - 14, x, y + 2);
      gradient.addColorStop(0, 'rgba(210,230,232,0)');
      gradient.addColorStop(1, `rgba(230,244,245,${star.alpha})`);
      c.strokeStyle = gradient;
      c.lineWidth = .55;
      c.beginPath();
      c.moveTo(x, y - 14);
      c.lineTo(x, y + 2);
      c.stroke();
    });

    this.shootingStars.forEach((star) => {
      if (time < star.start) return;
      const progress = (time - star.start) / star.duration;
      if (progress > 1) {
        this.resetShootingStar(star);
        return;
      }
      const headX = star.x * this.w + progress * this.w * .68;
      const headY = star.y * this.h + progress * this.h * star.angle;
      const tailX = headX - star.length * Math.cos(star.angle);
      const tailY = headY - star.length * Math.sin(star.angle);
      const fade = Math.sin(progress * Math.PI) * .56;
      const gradient = c.createLinearGradient(tailX, tailY, headX, headY);
      gradient.addColorStop(0, 'rgba(215,231,231,0)');
      gradient.addColorStop(1, `rgba(245,255,255,${fade})`);
      c.strokeStyle = gradient;
      c.lineWidth = .65;
      c.beginPath();
      c.moveTo(tailX, tailY);
      c.lineTo(headX, headY);
      c.stroke();
    });

    if (time >= this.nextShower && this.meteorShower.length === 0) {
      for (let i = 0; i < 13; i++) {
        this.meteorShower.push({
          start: time + i * 72 + Math.random() * 150,
          x: .48 + Math.random() * .55,
          y: -.12 + Math.random() * .27,
          duration: 580 + Math.random() * 520,
          length: 55 + Math.random() * 105
        });
      }
    }
    this.meteorShower = this.meteorShower.filter((meteor) => {
      const progress = (time - meteor.start) / meteor.duration;
      if (progress < 0) return true;
      if (progress > 1) return false;
      const headX = meteor.x * this.w - progress * this.w * .2;
      const headY = meteor.y * this.h + progress * this.h * .52;
      const fade = Math.sin(progress * Math.PI) * .38;
      const gradient = c.createLinearGradient(headX + meteor.length, headY - meteor.length * .68, headX, headY);
      gradient.addColorStop(0, 'rgba(220,238,240,0)');
      gradient.addColorStop(1, `rgba(235,249,250,${fade})`);
      c.strokeStyle = gradient;
      c.lineWidth = .65;
      c.beginPath();
      c.moveTo(headX + meteor.length, headY - meteor.length * .68);
      c.lineTo(headX, headY);
      c.stroke();
      return true;
    });
    if (this.meteorShower.length === 0 && time >= this.nextShower) this.nextShower = time + 9000 + Math.random() * 11000;
    this.drawUFO(time);
  }

  drawUFO(time) {
    const ufo = this.ufo;
    if (time < ufo.start) return;
    const progress = (time - ufo.start) / ufo.duration;
    if (progress > 1) {
      ufo.start = time + 11000 + Math.random() * 18000;
      ufo.duration = 5000 + Math.random() * 1500;
      return;
    }
    const c = this.ctx;
    const inverse = 1 - progress;
    const x = inverse * inverse * inverse * this.w * -.12 + 3 * inverse * inverse * progress * this.w * .2 + 3 * inverse * progress * progress * this.w * .88 + progress * progress * progress * this.w * 1.1;
    const y = inverse * inverse * inverse * this.h * .58 + 3 * inverse * inverse * progress * this.h * .12 + 3 * inverse * progress * progress * this.h * .2 + progress * progress * progress * this.h * .38;
    const opacity = Math.sin(progress * Math.PI) * .42;
    const hover = Math.sin(time * .006) * 2.5;
    c.save();
    c.translate(x, y + hover);
    c.rotate(-.08 + Math.sin(progress * Math.PI * 2) * .035);
    c.scale(.66 + Math.sin(progress * Math.PI) * .2, .66 + Math.sin(progress * Math.PI) * .2);
    c.globalAlpha = opacity;
    c.strokeStyle = '#cbd9da';
    c.fillStyle = 'rgba(188,207,210,.2)';
    c.shadowColor = 'rgba(133,200,211,.65)';
    c.shadowBlur = 13;
    c.lineWidth = .9;
    c.beginPath();
    c.ellipse(0, 1, 31, 7, 0, 0, Math.PI * 2);
    c.fill();
    c.stroke();
    c.beginPath();
    c.ellipse(0, -3, 13, 8, 0, Math.PI, Math.PI * 2);
    c.fill();
    c.stroke();
    const beam = c.createLinearGradient(0, 5, 0, 40);
    beam.addColorStop(0, 'rgba(172,225,221,.16)');
    beam.addColorStop(1, 'rgba(172,225,221,0)');
    c.fillStyle = beam;
    c.beginPath();
    c.moveTo(-17, 5);
    c.lineTo(17, 5);
    c.lineTo(29, 39);
    c.lineTo(-29, 39);
    c.closePath();
    c.fill();
    c.fillStyle = '#d8ff54';
    for (let light = -2; light <= 2; light++) {
      c.globalAlpha = opacity * (.55 + Math.sin(time * .01 + light) * .2);
      c.beginPath();
      c.arc(light * 9, 2.5, 1.15, 0, Math.PI * 2);
      c.fill();
    }
    c.restore();
  }

  updateGlobeLabel() {
    if (!this.ready || !this.markerObjects[this.active]) return;
    const THREE = this.THREE;
    const local = this.markerObjects[this.active].group.position.clone();
    const world = local.applyQuaternion(this.globeGroup.quaternion).add(this.globeGroup.position);
    const cameraSpace = world.clone().applyMatrix4(this.camera.matrixWorldInverse);
    const screen = world.clone().project(this.camera);
    const visible = cameraSpace.z < 0 && screen.z < 1;
    this.label.style.opacity = visible ? '1' : '0';
    this.label.style.left = `${(screen.x * .5 + .5) * this.w}px`;
    this.label.style.top = `${(-screen.y * .5 + .5) * this.h}px`;
    this.label.textContent = this.cities[this.active].name;
  }

  frame(time) {
    const delta = Math.min(32, time - this.lastTime || 16);
    this.lastTime = time;
    this.drawSpace(time);
    if (this.ready) {
      if (this.targetQuaternion && !this.reduced) this.globeGroup.quaternion.slerp(this.targetQuaternion, Math.min(.085, delta * .0042));
      if (this.clouds && !this.reduced) this.clouds.rotation.y += delta * .000015;
      this.markerObjects.forEach((marker, index) => {
        if (index === this.active) {
          const pulse = 1 + Math.sin(time * .004) * .13;
          marker.ring.scale.setScalar(1.5 * pulse);
          marker.halo.scale.setScalar(1 + Math.sin(time * .003) * .12);
        }
      });
      this.renderer.render(this.scene, this.camera);
      this.updateGlobeLabel();
    }
    this.raf = requestAnimationFrame((nextTime) => this.frame(nextTime));
  }
}

function initOMC(root = document) {
  root.querySelectorAll('[data-globe-hero]:not([data-initialized])').forEach((hero) => {
    hero.dataset.initialized = 'true';
    new OMCGlobe(hero);
  });
}

function initMessageRotators(root = document) {
  root.querySelectorAll('[data-message-rotator]:not([data-initialized])').forEach((rotator) => {
    rotator.dataset.initialized = 'true';
    const messages = [...rotator.querySelectorAll('[data-message]')];
    if (messages.length < 2 || matchMedia('(prefers-reduced-motion: reduce)').matches) return;
    let activeIndex = 0;
    const duration = Math.max(4000, Number(rotator.dataset.duration || 6000));
    setInterval(() => {
      const outgoing = messages[activeIndex];
      if (outgoing.classList.contains('omc-message--slow-exit')) {
        outgoing.classList.add('is-leaving-slow');
        setTimeout(() => outgoing.classList.remove('is-leaving-slow'), 2800);
      }
      outgoing.classList.remove('is-active');
      activeIndex = (activeIndex + 1) % messages.length;
      messages[activeIndex].classList.add('is-active');
    }, duration);
  });
}

function initPopups(root = document) {
  root.querySelectorAll('[data-email-popup]:not([data-initialized])').forEach((popup) => {
    popup.dataset.initialized = 'true';

    const storageKey = 'omcEmailPopupClosed';
    const scrollKey = 'omcEmailPopupScrollY';
    const inEditor = Boolean(window.Shopify && Shopify.designMode);
    const form = popup.querySelector('.omc-popup__form');
    const successMessage = popup.querySelector('[data-popup-success]');
    const reducedMotion = matchMedia('(prefers-reduced-motion: reduce)').matches;

    let hasClosed = false;
    let isOpen = false;
    let isClosing = false;
    let scrollY = window.scrollY;
    let previousBodyStyles = {};

    try {
      hasClosed = sessionStorage.getItem(storageKey) === 'true';
    } catch (error) {
      hasClosed = false;
    }

    const restoreScrollImmediately = (position) => {
      const previousBehavior = document.documentElement.style.scrollBehavior;
      document.documentElement.style.scrollBehavior = 'auto';
      window.scrollTo(0, position);
      document.documentElement.style.scrollBehavior = previousBehavior;
    };

    const lockPage = () => {
      scrollY = window.scrollY;

      previousBodyStyles = {
        position: document.body.style.position,
        top: document.body.style.top,
        width: document.body.style.width,
        overflow: document.body.style.overflow
      };

      document.body.style.position = 'fixed';
      document.body.style.top = `-${scrollY}px`;
      document.body.style.width = '100%';
      document.body.style.overflow = 'hidden';
      document.body.classList.add('omc-popup-open');
    };

    const unlockPage = () => {
      document.body.classList.remove('omc-popup-open');
      document.body.style.position = previousBodyStyles.position;
      document.body.style.top = previousBodyStyles.top;
      document.body.style.width = previousBodyStyles.width;
      document.body.style.overflow = previousBodyStyles.overflow;

      restoreScrollImmediately(scrollY);
    };

    const finishClose = (remember) => {
      popup.setAttribute('aria-hidden', 'true');
      unlockPage();
      isOpen = false;
      isClosing = false;

      if (remember) {
        try {
          sessionStorage.setItem(storageKey, 'true');
        } catch (error) {}
      }
    };

    const close = (remember = true) => {
      if (!isOpen || isClosing) return;

      isClosing = true;
      popup.classList.remove('is-visible');

      if (reducedMotion) {
        finishClose(remember);
        return;
      }

      let completed = false;

      const complete = () => {
        if (completed) return;
        completed = true;
        popup.removeEventListener('transitionend', handleTransitionEnd);
        finishClose(remember);
      };

      const handleTransitionEnd = (event) => {
        if (event.target === popup && event.propertyName === 'opacity') {
          complete();
        }
      };

      popup.addEventListener('transitionend', handleTransitionEnd);
      window.setTimeout(complete, 350);
    };

    const open = () => {
      if (isOpen) return;

      isOpen = true;
      lockPage();
      popup.setAttribute('aria-hidden', 'false');

      requestAnimationFrame(() => {
        popup.classList.add('is-visible');
      });
    };

    popup.querySelectorAll('[data-popup-close]').forEach((button) => {
      button.addEventListener('click', () => close(true));
    });

    document.addEventListener('keydown', (event) => {
      if (event.key === 'Escape' && isOpen) {
        close(true);
      }
    });

    if (form) {
      form.addEventListener('submit', () => {
        try {
          sessionStorage.setItem(scrollKey, String(scrollY));
        } catch (error) {}
      });
    }

    if (successMessage) {
      try {
        const savedScroll = Number(sessionStorage.getItem(scrollKey));

        if (Number.isFinite(savedScroll)) {
          scrollY = savedScroll;
          restoreScrollImmediately(savedScroll);
          sessionStorage.removeItem(scrollKey);
        }
      } catch (error) {}

      open();
      window.setTimeout(() => close(true), 1400);
      return;
    }

    if (hasClosed && !inEditor) return;

    window.setTimeout(
      open,
      inEditor ? 500 : Number(popup.dataset.delay || 8000)
    );
  });
}

function initMotion() {
  const reduced = matchMedia('(prefers-reduced-motion: reduce)').matches;
  const loader = document.querySelector('[data-omc-loader]');
  if (loader) {
    const count = loader.querySelector('[data-loader-count]');
    let value = 0;
    const loading = setInterval(() => {
      value = Math.min(100, value + Math.ceil(Math.random() * 12));
      count.textContent = String(value).padStart(3, '0');
      if (value === 100) {
        clearInterval(loading);
        setTimeout(() => loader.classList.add('is-complete'), 260);
      }
    }, reduced ? 10 : 70);
  }
  if (!reduced) {
    const observer = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          entry.target.classList.add('is-visible');
          observer.unobserve(entry.target);
        }
      });
    }, { threshold: .12 });
    document.querySelectorAll('[data-reveal-section], .omc-reveal').forEach((element) => observer.observe(element));
  } else {
    document.querySelectorAll('[data-reveal-section], .omc-reveal').forEach((element) => element.classList.add('is-visible'));
    if (loader) loader.classList.add('is-complete');
  }
}

initOMC();
initMessageRotators();
initPopups();
initMotion();
document.addEventListener('shopify:section:load', (event) => {
  initOMC(event.target);
  initMessageRotators(event.target);
  initPopups(event.target);
});
